import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeNl from '@angular/common/locales/nl';
import localeTr from '@angular/common/locales/tr';
import {
    AfterViewInit,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    DestroyRef,
    effect,
    inject,
    Injector,
    linkedSignal,
    OnInit,
    Signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { IUser } from '@shs-digi-care-monorepo/interfaces';
import { tap } from 'rxjs';
import type {
    UserInfoDialogComponent,
    UserInfoDialogData,
    UserInfoDialogResult
} from './shared/components/user-info-dialog/user-info-dialog.component';
import { HeaderComponent } from './shared/components/header/header.component';
import {
    ActionRequiredComponent,
    BannerComponent,
    FooterComponent
} from '@shs-digi-care-monorepo/frontend-lib/components';
import { EnvironmentHintService } from '@shs-digi-care-monorepo/frontend-lib/services';
import { environment } from '../environments/environment';
import { ShSnackbarContainerComponent } from '@shs-digi-care-monorepo/frontend-lib/ui/sh-snackbar/sh-snackbar-container.component';
import { TeamActions, UserActions, UserState } from '@shs-digi-care-monorepo/frontend-lib/states';
import { LoggingService } from '@shs-digi-care-monorepo/frontend-lib/logging';

const DEFAULT_LANGUAGE = 'en';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    imports: [
        RouterOutlet,
        FooterComponent,
        HeaderComponent,
        ShSnackbarContainerComponent,
        ActionRequiredComponent,
        BannerComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppComponent implements OnInit, AfterViewInit {
    private store = inject(Store);
    private readonly translate: TranslateService = inject(TranslateService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly logger = inject(LoggingService);
    protected readonly injector = inject(Injector);
    protected readonly dialog = inject(MatDialog);

    protected readonly stage = environment.stage;
    protected readonly showEnvironmentHint = inject(EnvironmentHintService).showEnvironmentHint;
    protected readonly me: Signal<IUser | null> = this.store.selectSignal(UserState.me);
    private readonly userLoadedFromState: Signal<boolean> = this.store.selectSignal(UserState.loaded);
    /**
     * Latches to `true` once the user has been loaded for the first time and never
     * reverts to `false`, so the startup spinner is shown only once and never reappears
     * during navigation or any subsequent state changes.
     */
    protected readonly userLoaded: Signal<boolean> = linkedSignal<boolean, boolean>({
        source: this.userLoadedFromState,
        computation: (loaded, previous) => previous?.value || loaded
    });

    public ngOnInit(): void {
        this.logger.info('Application initialized');
        this.setDefaultLanguage();
        this.listenToLanguageChanges();
        this.registerLocale();
        this.loadTeams();
    }

    public ngAfterViewInit(): void {
        this.displayIncompleteProfileInfoPopup();
    }

    private setDefaultLanguage(): void {
        this.translate.setDefaultLang(DEFAULT_LANGUAGE);
        this.translate.use(DEFAULT_LANGUAGE);
    }

    private listenToLanguageChanges(): void {
        this.translate.onLangChange
            .pipe(
                tap((event) => {
                    this.logger.debug('Language changed', { lang: event.lang });
                    localStorage.setItem('language', event.lang);
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private loadTeams(): void {
        this.store.dispatch(new TeamActions.LoadAll());
    }

    private registerLocale(): void {
        registerLocaleData(localeDe);
        registerLocaleData(localeTr);
        registerLocaleData(localeNl);
    }

    private displayIncompleteProfileInfoPopup(): void {
        effect(
            async () => {
                const user = this.me();
                const profileSetupLater = sessionStorage.getItem('profileSetupLater');
                if (user && !user.userProfileComplete && profileSetupLater !== 'true') {
                    const { UserInfoDialogComponent } = await import(
                        './shared/components/user-info-dialog/user-info-dialog.component'
                    );
                    this.dialog
                        .open<UserInfoDialogComponent, UserInfoDialogData, UserInfoDialogResult>(
                            UserInfoDialogComponent,
                            { data: user }
                        )
                        .afterClosed()
                        .pipe(
                            tap((result) => {
                                if (result) {
                                    this.store.dispatch(new UserActions.PatchMe(result));
                                } else {
                                    sessionStorage.setItem('profileSetupLater', 'true');
                                }
                            })
                        )
                        .subscribe();
                }
            },
            { injector: this.injector }
        );
    }
}
